import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api'
import { useMeta } from '../meta'
import Pager from '../components/Pager'
import type { HomeRow, Paged } from '../types'
import { staleness } from '../util'

const LINK_CHIP: Record<string, { cls: string; label: string }> = {
  ok: { cls: 'on', label: 'Connected' },
  reauth_required: { cls: 'danger', label: 'Needs reconnect' },
  revoked: { cls: 'danger', label: 'Disconnected' },
  offline: { cls: '', label: 'Offline' },
}

const LIMIT = 24

export default function Homes() {
  const nav = useNavigate()
  const { poll_interval_s: poll } = useMeta()
  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)

  const { data, error, isLoading } = useQuery<Paged<HomeRow>>({
    queryKey: ['homes', q, offset],
    queryFn: () => api<Paged<HomeRow>>(
      `/admin/homes?limit=${LIMIT}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    refetchInterval: 60_000,
  })

  const rows = data?.rows ?? []
  const attention = rows.filter((r) => r.link_status && r.link_status !== 'ok')

  return (
    <>
      <h1>Homes</h1>
      <p className="sub">
        Every connected household. Open one to see its thermostats, their
        history and what the control loop has been doing.
      </p>

      <div className="row" style={{ marginBottom: '1rem' }}>
        <input placeholder="Search home, city, ZIP or owner" value={q}
               style={{ minWidth: 280 }}
               onChange={(e) => { setQ(e.target.value); setOffset(0) }} />
        {data && (
          <span className="muted">
            {data.total.toLocaleString()} home{data.total === 1 ? '' : 's'}
            {attention.length > 0 && ` · ${attention.length} need attention`}
          </span>
        )}
      </div>

      {error && <div className="err">{(error as Error).message}</div>}
      {isLoading && <p className="muted">Loading…</p>}

      {data && rows.length === 0 && (
        <div className="panel"><div className="empty">
          <h3>{q ? 'Nothing matches that' : 'No homes yet'}</h3>
          <p>{q ? 'Try a different name, city or address.'
                : 'A home appears here as soon as a customer adds one.'}</p>
        </div></div>
      )}

      <div className="cards">
        {rows.map((r) => {
          const s = staleness(r.last_reading_at, poll)
          const link = r.link_status ? LINK_CHIP[r.link_status] : null
          return (
            <button className="card" key={r.home_id}
                    onClick={() => nav(`/homes/${r.home_id}`)}>
              <div className="top">
                <div>
                  <div className="name">{r.name}</div>
                  <div className="where">
                    {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                    {r.postal_code && ` · ${r.postal_code}`}
                  </div>
                </div>
                {link && <span className={`chip ${link.cls}`}>{link.label}</span>}
              </div>

              <div className="where" style={{ marginTop: -4 }}>{r.email}</div>

              <div className="facts">
                <div className="fact">
                  <div className="k2">Thermostats</div>
                  <div className="v2">{r.devices}</div>
                </div>
                <div className="fact">
                  <div className="k2">Control</div>
                  <div className="v2">
                    {r.controlled > 0
                      ? <span className="chip on">{r.controlled} armed</span>
                      : <span className="muted">Shadow</span>}
                  </div>
                </div>
                <div className="fact">
                  <div className="k2">Plan</div>
                  <div className="v2">
                    {r.entitled ? 'Active' : <span className="muted">None</span>}
                  </div>
                </div>
                <div className="fact">
                  <div className="k2">Last reading</div>
                  <div className={`v2 ${s.cls === 'danger' ? 'danger' : ''}`}>
                    {s.label}
                  </div>
                </div>
              </div>

              {r.last_error && (
                <div className="err" style={{ margin: 0, fontSize: 12 }}>
                  {r.last_error}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {data && (
        <div className="panel">
          <Pager total={data.total} limit={LIMIT} offset={offset}
                 onOffset={setOffset} noun="home" />
        </div>
      )}
    </>
  )
}
